import React, { useEffect, useState } from "react";
import { Bell, Trash2, AlertTriangle, Info, OctagonAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { timeAgo } from "@/lib/format";

const SEVERITY = {
  critical: { color: "text-[#EF4444]", bg: "bg-[#EF4444]/10 border-[#EF4444]/30", Icon: OctagonAlert },
  warning: { color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10 border-[#F59E0B]/30", Icon: AlertTriangle },
  info: { color: "text-[#3B82F6]", bg: "bg-[#3B82F6]/10 border-[#3B82F6]/30", Icon: Info },
};

export default function ActivityPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/activity");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const clearAll = async () => {
    if (!confirm("Cancellare tutti gli alert?")) return;
    try {
      await api.delete("/activity");
      toast.success("Activity log cancellato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10B981] mb-1.5">
            // Alerts &amp; Events
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Activity
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Tutti gli alert generati dal sistema (anche quelli inviati su Telegram).
          </p>
        </div>
        {items.length > 0 && (
          <button
            onClick={clearAll}
            data-testid="activity-clear-btn"
            className="flex items-center gap-1.5 bg-[#18181B] hover:bg-[#27272A] text-[#A1A1AA] hover:text-[#EF4444] border border-[#27272A] px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-wider"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="font-mono text-sm text-[#A1A1AA]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bento p-12 text-center">
          <Bell className="w-10 h-10 text-[#A1A1AA] mx-auto mb-3" />
          <h3 className="font-heading font-bold text-xl mb-2">Nessun alert</h3>
          <p className="text-sm text-[#A1A1AA]">
            I tuoi rig stanno funzionando regolarmente 🟢
          </p>
        </div>
      ) : (
        <div className="space-y-2 stagger-fade">
          {items.map((a) => {
            const cfg = SEVERITY[a.severity] || SEVERITY.warning;
            const Icon = cfg.Icon;
            return (
              <div
                key={a.id}
                data-testid={`activity-${a.id}`}
                className={`bento p-4 border-l-2 ${cfg.bg.split(" ")[1]} flex items-start gap-3`}
              >
                <Icon className={`w-5 h-5 ${cfg.color} flex-shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-sm">
                      {a.rig_name}
                    </span>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${cfg.color}`}>
                      {a.severity}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA]">
                      {a.type}
                    </span>
                  </div>
                  <div className="text-sm text-[#FAFAFA] mt-1">{a.message}</div>
                  <div className="flex items-center gap-3 font-mono text-[10px] text-[#A1A1AA] mt-2">
                    <span>{timeAgo(a.timestamp)}</span>
                    <span className="text-[#27272A]">·</span>
                    <span>
                      Telegram: {a.telegram_sent ? <span className="text-[#10B981]">sent</span> : <span className="text-[#A1A1AA]">skipped</span>}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
